"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_SRC =
	"/image/new-version/ValueAddedSolutions/surface-finishing/surface-finishing-services-with-js-precision.webp";

const FEATURES = [
	{ keyPrefix: "precisionFeature1" },
	{ keyPrefix: "precisionFeature2" },
	{ keyPrefix: "precisionFeature3" },
] as const;

const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${className ?? ""}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

const CheckIcon = () => (
	<span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-themePrimary400 text-white">
		<svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden="true">
			<path d="M4 10.5 8 14l8-9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	</span>
);

export default function SurfaceFinishingPrecision() {
	const t = useTranslations("new-surface-finishing");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="relative mb-8 flex min-h-[72px] items-center sm:mb-10 sm:min-h-[80px] md:mb-12 md:min-h-[88px]">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{t("precisionTitleLine1")}</span>
						<span className="block">{t("precisionTitleLine2")}</span>
					</h2>
					<div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
						<GlobeIcon className="text-themePrimary400" />
					</div>
				</div>

				<div className="grid items-center gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
					<div className="relative aspect-[4/3] w-full overflow-hidden bg-[#eeeeee]">
						<Image
							src={IMAGE_SRC}
							alt={t("precisionImageAlt")}
							fill
							className="object-cover"
							sizes="(max-width: 1024px) 100vw, 52vw"
						/>
					</div>

					<div className="space-y-6">
						<div className="space-y-6">
							{FEATURES.map((feature) => (
								<div key={feature.keyPrefix} className="flex gap-3">
									<CheckIcon />
									<div>
										<h3 className="mb-2 text-base font-bold text-black sm:text-lg">
											{t(`${feature.keyPrefix}Title`)}
										</h3>
										<p className="text-sm leading-relaxed text-black md:text-base">
											{t(`${feature.keyPrefix}Desc`)}
										</p>
									</div>
								</div>
							))}
						</div>

						<Link
							href="/upload"
							className="inline-flex items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#83b751] hover:text-white md:px-8 md:py-3.5 md:text-base"
						>
							{t("precisionGetInstantQuote")}
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
}
